import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import type {
  DmOfferForm,
  DmThread,
  MasterContractOfferForm,
  SubcontractOfferForm,
} from "../services/dmThreads";
import {
  createInitialOfferWizardEntry,
  OfferDraftSummary,
  OfferDraftSyncStatus,
  OfferPrefillSource,
  OfferStep,
  OfferWizardData,
  OfferWizardEntry,
  StepOrder,
} from "../../components/offer-wizard/types";

/**
 * 1スレッド分のオファー作成ウィザード状態を `byThreadKey` で保持する。
 *
 * threadKey 規約:
 * - ドラフト（受信者未選択でまだサーバ未保存）: `draft:<uuid>`
 *   オファーボタン押下のたびに新しいドラフトキーを発行する。
 * - サーバ側にドラフトレコードが存在するスレッド: `dm:<dmThreadId>`
 *   `POST /api/me/<project_id>/dm/` 成功時、または既存ドラフトのハイドレート時に使用。
 *
 * `draft:<uuid>` のキーは `assignServerDmId` によって `dm:<dmThreadId>` にリネームされる。
 *
 * 各ドラフトは ThreadList 上に擬似スレッドとして並び、選択すると右カラムに
 * オーバーレイ表示する。別スレッドへ切り替えても各ドラフトの入力途中状態を
 * 保持する。
 */
export interface OfferWizardSliceState {
  byThreadKey: Record<string, OfferWizardEntry>;
}

/** dm_id から canonical な threadKey を組み立てる */
export const dmThreadKey = (dmId: string): string => `dm:${dmId}`;

const initialState: OfferWizardSliceState = {
  byThreadKey: {},
};

/** crypto.randomUUID() をフォールバック付きで利用する */
const newDraftKey = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `draft:${crypto.randomUUID()}`;
  }
  return `draft:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const offerWizardSlice = createSlice({
  name: "offerWizard",
  initialState,
  reducers: {
    /**
     * 新規ドラフトを作成して開く。`threadKey` は内部で発行し、
     * `meta` で呼び出し側に返す（slice 内では state 更新のみ）。
     * 呼び出し側は `prepare` で生成した `threadKey` を action.payload から取得する。
     */
    startDraft: {
      reducer: (
        state,
        action: PayloadAction<{ threadKey: string; projectId: string }>,
      ) => {
        const { threadKey, projectId } = action.payload;
        state.byThreadKey[threadKey] = createInitialOfferWizardEntry(projectId);
      },
      prepare: (projectId: string) => ({
        payload: { threadKey: newDraftKey(), projectId },
      }),
    },

    /** 既存ドラフトを選択して開く */
    openDraft: (state, action: PayloadAction<{ threadKey: string }>) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (entry) {
        entry.isOpen = true;
      }
    },

    /** ドラフト選択を解除する（最小化）。状態は保持。 */
    closeDraft: (state, action: PayloadAction<{ threadKey: string }>) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (entry) {
        entry.isOpen = false;
      }
    },

    /** ドラフトを完全に破棄する。 */
    discardWizard: (state, action: PayloadAction<{ threadKey: string }>) => {
      delete state.byThreadKey[action.payload.threadKey];
    },

    /** 現在のステップを設定する。 */
    setStep: (
      state,
      action: PayloadAction<{ threadKey: string; step: OfferStep }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (entry) {
        entry.currentStep = action.payload.step;
      }
    },

    /** ステップナビゲーションの並び順（送信先 / ロール の前後関係）を設定する。 */
    setStepOrder: (
      state,
      action: PayloadAction<{ threadKey: string; stepOrder: StepOrder }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (entry) {
        entry.stepOrder = action.payload.stepOrder;
      }
    },

    /**
     * フォームデータを部分更新する。トップレベルキーごとに浅くマージする。
     */
    patchData: (
      state,
      action: PayloadAction<{
        threadKey: string;
        patch: Partial<OfferWizardData>;
      }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (!entry) return;
      entry.data = { ...entry.data, ...action.payload.patch };
    },

    /** roleId のみを更新する */
    setRoleId: (
      state,
      action: PayloadAction<{ threadKey: string; roleId: number | undefined }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (!entry) return;
      entry.data.roleId = action.payload.roleId;
    },

    /** スレッド一覧表示用のサマリを部分更新する */
    patchSummary: (
      state,
      action: PayloadAction<{
        threadKey: string;
        patch: Partial<OfferDraftSummary>;
      }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (!entry) return;
      entry.data.summary = { ...entry.data.summary, ...action.payload.patch };
    },

    /**
     * `POST /api/me/<project_id>/dm/` の成功直後に呼ぶ。
     * `draft:<uuid>` キーで保持していた entry を `dm:<dm_id>` キーに移し替え、
     * `serverDmId` を埋める。`fromKey === toKey` の場合は no-op。
     */
    assignServerDmId: (
      state,
      action: PayloadAction<{ fromKey: string; dmId: string }>,
    ) => {
      const { fromKey, dmId } = action.payload;
      const entry = state.byThreadKey[fromKey];
      if (!entry) return;
      const toKey = dmThreadKey(dmId);
      if (fromKey === toKey) {
        entry.serverDmId = dmId;
        return;
      }
      // 移し替え + 旧キー削除
      state.byThreadKey[toKey] = {
        ...entry,
        serverDmId: dmId,
      };
      delete state.byThreadKey[fromKey];
    },

    /**
     * `getDmThreads({ status: 'draft' (新) / 'new' (旧), role: 'requester' })`
     * 結果から、Redux 側に未登録の DmThread を `dm:<id>` キーで取り込む（minimal
     * hydrate）。呼び出し側（`DM.tsx`）で `isOfferDraftStatus` により旧新両値を
     * フィルタしてから dispatch する。
     * 既存 entry がある場合は `serverDmId` / `projectId` を補完して上書きしない。
     *
     * 一覧 API には `offer_form` 等の Detail 拡張ブロックは含まれないため、
     * 本 reducer ではスレッド直下のフィールドのみを反映する。`offer_form` 由来の
     * 詳細フィールドは `hydrateOfferFormFromDetail` で別途取り込む。
     *
     * `projectId` を必ず受け取って entry に格納すること（`selectOfferDraftsByProject`
     * で空文字 fallback すると ThreadList の Draft 一覧から漏れる）。
     */
    hydrateDraftFromServer: (
      state,
      action: PayloadAction<{ thread: DmThread; projectId: string }>,
    ) => {
      const { thread, projectId } = action.payload;
      const key = dmThreadKey(thread.id);
      if (state.byThreadKey[key]) {
        // 既存 entry に serverDmId / projectId が無ければ補完
        const existing = state.byThreadKey[key];
        if (!existing.serverDmId) existing.serverDmId = thread.id;
        if (!existing.projectId) existing.projectId = projectId;
        return;
      }
      // requester 自身が見ている前提で contractor 側のユーザー情報をサマリ化。
      // contractor が null なら email 経由ドラフト（`offer_form.recipient_email` で
      // 宛先を保持）と判断。実際のメール文字列は Detail GET 後に
      // `hydrateOfferFormFromDetail` で埋まる。
      const isEmailDraft =
        thread.thread_type === "subcontract" && !thread.contractor;
      const recipientLabel =
        thread.contractor?.display_name ?? (isEmailDraft ? "メール経由オファー" : "新規オファー");
      const scope = thread.scope && thread.scope.length > 0
        ? thread.scope
        : undefined;
      state.byThreadKey[key] = {
        isOpen: false,
        currentStep: "form",
        data: {
          recipient: isEmailDraft
            ? { mode: "email" }
            : {
                mode: "follows",
                userId: thread.contractor?.id,
              },
          form: {
            allowSubcontracting: thread.allow_subcontracting ?? false,
            acceptPosition: thread.accept_position ?? null,
            advancePaymentRate: thread.advance_payment_rate,
            requiresDeliverable: thread.requires_deliverable,
            isTransactional: thread.is_transactional,
            scope,
            deadline: thread.deadline ?? undefined,
            message: thread.initial_message ?? undefined,
            details: {
              contractStartDate: thread.contract_start_date,
              contractEndDate: thread.contract_end_date,
            },
          },
          summary: {
            recipientLabel,
            recipientAvatarUrl: thread.contractor?.avatar_url ?? null,
            recipientName: recipientLabel,
            scope,
            roleLabel: thread.assigned_roles ?? undefined,
          },
        },
        stepOrder: "recipient_first",
        projectId,
        createdAt: thread.created_at,
        serverDmId: thread.id,
        // ハイドレートはサーバ取得を反映しただけで「ユーザー操作で保存」した
        // わけではないので "saved" は出さず idle にしておく。
        syncStatus: "idle",
      };
    },

    /**
     * `getDmThreadDetail` のレスポンスから `offer_form` ブロック（および thread の
     * 一部詳細）を既存 entry にマージする。
     *
     * 仕様書 §3-2 より `offer_form` は thread_type に応じて 2 種類の形がある:
     *  - subcontract: `special_provisions` / `quantity` / `deliverable_format` /
     *    `delivery_method` / `delivery_location` / `valid_until` / `initial_message`
     *  - master_contract: `proposed_budget` / `contract_type` / `ownership_type` /
     *    `disclosure_rule` / `initial_message`
     *
     * 「ローカル入力を上書きしない」制御:
     *   各フィールドは現在値が `undefined` のときだけサーバ値で埋める。ユーザーが
     *   既に編集したフィールド（空文字列を含む）は触らない。`syncStatus` には
     *   依存しないので、初回ドラフト作成直後（`synced`）に Detail が届いても
     *   ハイドレートが取り逃しなく適用される。
     */
    hydrateOfferFormFromDetail: (
      state,
      action: PayloadAction<{
        dmId: string;
        thread: DmThread;
        offerForm: DmOfferForm | null;
      }>,
    ) => {
      const { dmId, thread, offerForm } = action.payload;
      const key = dmThreadKey(dmId);
      const entry = state.byThreadKey[key];
      if (!entry) return;

      const form = entry.data.form;
      const nextForm = { ...form };
      const nextDetails = { ...(form.details ?? {}) };

      // ─── thread 直下のフィールドをマージ（フォームに未入力のものだけ補完） ───
      if (nextForm.scope === undefined && thread.scope) nextForm.scope = thread.scope;
      if (nextForm.deadline === undefined && thread.deadline) {
        nextForm.deadline = thread.deadline;
      }
      if (nextForm.acceptPosition === undefined) {
        nextForm.acceptPosition = thread.accept_position ?? null;
      }
      if (nextForm.advancePaymentRate === undefined) {
        nextForm.advancePaymentRate = thread.advance_payment_rate;
      }
      if (nextForm.requiresDeliverable === undefined) {
        nextForm.requiresDeliverable = thread.requires_deliverable;
      }
      if (nextForm.isTransactional === undefined) {
        nextForm.isTransactional = thread.is_transactional;
      }
      if (nextDetails.contractStartDate === undefined) {
        nextDetails.contractStartDate = thread.contract_start_date;
      }
      if (nextDetails.contractEndDate === undefined) {
        nextDetails.contractEndDate = thread.contract_end_date;
      }

      // ─── 対価行のマージ ───
      // 仕様書 §3-2 で `offer_form.compensation_lines` が永続化対象になった。
      // 明細があればそれを復元、空配列のレガシードラフトには `thread.contract_amount`
      // を 1 行に展開してフォールバックする（プリフィルテンプレートで上書きされるのを防ぐ）。
      if (nextForm.compensationLines === undefined) {
        const apiLines =
          (offerForm as
            | SubcontractOfferForm
            | MasterContractOfferForm
            | null
          )?.compensation_lines ?? [];
        if (apiLines.length > 0) {
          nextForm.compensationLines = apiLines.map((l) => ({
            label: l.label,
            unitPrice: l.unit_price,
            quantity: l.quantity,
            unit: l.unit,
          }));
        } else if (
          thread.contract_amount !== null &&
          thread.contract_amount !== undefined
        ) {
          nextForm.compensationLines = [
            {
              label: "委託費",
              unitPrice: String(thread.contract_amount),
              quantity: "1",
              unit: "式",
            },
          ];
        }
      }

      // ─── offer_form をマージ（thread_type で分岐） ───
      if (offerForm && thread.thread_type === "subcontract") {
        const sf = offerForm as SubcontractOfferForm;
        if (nextForm.message === undefined && sf.initial_message !== null) {
          nextForm.message = sf.initial_message ?? undefined;
        }
        if (
          nextForm.specialProvisionsText === undefined &&
          sf.special_provisions !== null
        ) {
          nextForm.specialProvisionsText = sf.special_provisions ?? undefined;
        }
        if (nextForm.quantity === undefined && sf.quantity !== null) {
          nextForm.quantity = sf.quantity ?? undefined;
        }
        if (
          nextForm.deliverableFormat === undefined &&
          sf.deliverable_format !== null
        ) {
          nextForm.deliverableFormat = sf.deliverable_format ?? undefined;
        }
        if (
          nextDetails.deliveryMethod === undefined &&
          sf.delivery_method !== null
        ) {
          nextDetails.deliveryMethod = sf.delivery_method ?? undefined;
        }
        if (
          nextDetails.deliveryLocation === undefined &&
          sf.delivery_location !== null
        ) {
          nextDetails.deliveryLocation = sf.delivery_location ?? undefined;
        }
        if (nextDetails.validUntil === undefined && sf.valid_until !== null) {
          nextDetails.validUntil = sf.valid_until ?? undefined;
        }
        // メール経由オファー宛先メールが BE に保存されているなら、
        // wizard の recipient 状態を email モードに整合させる。
        if (
          sf.recipient_email &&
          (entry.data.recipient.mode !== "email" ||
            !entry.data.recipient.email)
        ) {
          entry.data.recipient = {
            mode: "email",
            email: sf.recipient_email,
          };
        }
      } else if (offerForm && thread.thread_type === "master_contract") {
        const mf = offerForm as MasterContractOfferForm;
        if (nextForm.message === undefined && mf.initial_message !== null) {
          nextForm.message = mf.initial_message ?? undefined;
        }
        nextForm.masterOfferForm = {
          proposedBudget: mf.proposed_budget,
          contractType: mf.contract_type,
          ownershipType: mf.ownership_type,
          disclosureRule: mf.disclosure_rule,
        };
      }

      nextForm.details = nextDetails;
      entry.data.form = nextForm;
    },

    /**
     * 詳細フォームのプリフィル元（テンプレート / 過去オファー）を設定する。
     * フォーム値そのものの反映は `DetailFormStep` 側で `patchData` 経由に行う。
     */
    setPrefillSource: (
      state,
      action: PayloadAction<{
        threadKey: string;
        source: OfferPrefillSource;
      }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (!entry) return;
      entry.prefillSource = action.payload.source;
    },

    /** サーバー同期状態を更新する（保存中インジケータ表示などに利用） */
    setSyncStatus: (
      state,
      action: PayloadAction<{
        threadKey: string;
        syncStatus: OfferDraftSyncStatus;
        lastSyncedAt?: string;
      }>,
    ) => {
      const entry = state.byThreadKey[action.payload.threadKey];
      if (!entry) return;
      entry.syncStatus = action.payload.syncStatus;
      if (action.payload.lastSyncedAt) {
        entry.lastSyncedAt = action.payload.lastSyncedAt;
      }
    },
  },
});

export const {
  startDraft,
  openDraft,
  closeDraft,
  discardWizard,
  setStep,
  setStepOrder,
  patchData,
  setRoleId,
  patchSummary,
  assignServerDmId,
  hydrateDraftFromServer,
  hydrateOfferFormFromDetail,
  setPrefillSource,
  setSyncStatus,
} = offerWizardSlice.actions;

export default offerWizardSlice.reducer;

// ===== セレクタ =====

export const selectOfferWizardEntry =
  (threadKey: string | null) =>
  (state: { offerWizard: OfferWizardSliceState }): OfferWizardEntry | undefined =>
    threadKey ? state.offerWizard.byThreadKey[threadKey] : undefined;

/**
 * 現在いずれかのドラフトオーバーレイが開いているか。
 * GlobalAudioToggle 等のフローティング UI を抑止するために利用する。
 */
export const selectIsAnyOfferDraftOpen = (state: {
  offerWizard: OfferWizardSliceState;
}): boolean =>
  Object.values(state.offerWizard.byThreadKey).some((entry) => entry.isOpen);

type OfferDraftListItem = { threadKey: string; entry: OfferWizardEntry };

const EMPTY_OFFER_DRAFTS: readonly OfferDraftListItem[] = [];

const selectByThreadKey = (state: {
  offerWizard: OfferWizardSliceState;
}) => state.offerWizard.byThreadKey;

// projectId ごとにメモ化セレクタをキャッシュする。毎回 createSelector を作り直すと
// レンダー間でキャッシュが効かず「同じ引数で異なる結果」を返して Redux 警告になるため。
const draftsByProjectSelectorCache = new Map<
  string,
  (state: { offerWizard: OfferWizardSliceState }) => OfferDraftListItem[]
>();

export const selectOfferDraftsByProject = (projectId: string | undefined) => {
  const cacheKey = projectId ?? "__none__";
  let selector = draftsByProjectSelectorCache.get(cacheKey);
  if (!selector) {
    selector = createSelector([selectByThreadKey], (byThreadKey) => {
      if (!projectId) return EMPTY_OFFER_DRAFTS as OfferDraftListItem[];
      return Object.entries(byThreadKey)
        .filter(([, entry]) => entry.projectId === projectId)
        .map(([threadKey, entry]) => ({ threadKey, entry }))
        .sort(
          (a, b) =>
            new Date(b.entry.createdAt).getTime() -
            new Date(a.entry.createdAt).getTime(),
        );
    });
    draftsByProjectSelectorCache.set(cacheKey, selector);
  }
  return selector;
};

/**
 * 全プロジェクト横断の全オファードラフト。残高はユーザー単位（全プロジェクト共通）
 * のため、「未送信ドラフトが予約している対価合計」を残高チェックで差し引く用途に使う。
 */
export const selectAllOfferDrafts = createSelector(
  [selectByThreadKey],
  (byThreadKey): OfferDraftListItem[] =>
    Object.entries(byThreadKey).map(([threadKey, entry]) => ({
      threadKey,
      entry,
    })),
);
